import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import type { Bounds, ObjectId, Vec2 } from '@/types';
import {
  reduceInteraction,
  isGesture,
  IDLE,
  type InteractionEffect,
  type InteractionState,
} from '@/core/interaction/pointerStateMachine';
import { PointerCoalescer } from '@/core/interaction/pointerCoalescer';
import type { PinchPoints } from '@/core/interaction/pinchGesture';
import { resizeFromHandle, rotationFromDrag, boxCentre, type ResizeHandle } from '@/core/interaction/manipulation';
import { snapBounds, shiftBounds } from '@/core/interaction/snapping';
import { objectType } from '@/core/scene/objectTypes';
import { IDENTITY_MATRIX, applyMatrix, invert } from '@/core/geometry/transform';
import { expand } from '@/core/geometry/bounds';
import { unionBounds } from '@/core/camera/cameraOps';
import { screenToWorld } from '@/core/coordinates/coordinateSystem';
import { useCameraStore } from '@/store/cameraStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useInteractionStore } from '@/store/interactionStore';
import { useWindowStore } from '@/store/windowStore';
import { useWorkspaceStore, selectActiveDocument } from '@/store/workspaceStore';
import {
  moveObjectsCommand,
  bringToFrontCommand,
  setTransformCommand,
  collectSubtrees,
  topLevelIds,
} from '@/features/objects/objectCommands';
import { addConnectionCommand, reconnectCommand } from '@/features/connections/connectionCommands';
import { hitTestConnections } from '@/features/connections/connectionHitTest';
import type { ConnectionId } from '@/types';
import type { Reconnect } from '@/core/interaction/pointerStateMachine';
import { isMeaningfulMarquee, marqueeBounds } from '@/features/selection/marquee';
import { CAMERA_LIMITS } from '@/constants/camera';
import { GRID_SIZE, SNAP_TOLERANCE_PX, CONNECTION_HIT_PX } from '@/constants/objects';
import { useEngine } from '@/app/EngineContext';

export interface CanvasHandlers {
  onPointerDown(e: ReactPointerEvent<HTMLElement>): void;
  onPointerMove(e: ReactPointerEvent<HTMLElement>): void;
  onPointerUp(e: ReactPointerEvent<HTMLElement>): void;
  onPointerCancel(e: ReactPointerEvent<HTMLElement>): void;
  onPointerLeave(e: ReactPointerEvent<HTMLElement>): void;
  onLostPointerCapture(e: ReactPointerEvent<HTMLElement>): void;
}

/** Exposed to the interaction layer so handles can start resize/rotate gestures. */
export interface CanvasInteractionController {
  readonly handlers: CanvasHandlers;
  readonly beginHandleDrag: (
    e: ReactPointerEvent<HTMLElement>,
    objectId: ObjectId,
    handle: ResizeHandle | 'rotate',
  ) => void;
  /** Starts dragging one end of a connection to another object. */
  readonly beginReconnect: (
    e: ReactPointerEvent<HTMLElement>,
    connectionId: ConnectionId,
    end: Reconnect['end'],
  ) => void;
}

interface MoveSample {
  readonly pointerId: number;
  readonly screen: Vec2;
}

const pinchPoints = (pointers: ReadonlyMap<number, Vec2>): PinchPoints | null => {
  const [a, b] = [...pointers.values()];
  return a && b ? { a, b } : null;
};

interface Modifiers {
  space: boolean;
  shift: boolean;
  alt: boolean;
}

/**
 * Binds raw pointer/wheel/keyboard events to the interaction state machine and turns
 * the resulting effects into store updates and commands. The machine knows nothing
 * about React or Zustand; this hook is the adapter.
 *
 * Per pointer move, at most: one machine transition, one snap computation, one
 * command dispatch, and writes to the interaction store (hover/guides/preview) that
 * only their narrow subscribers observe. The React tree is otherwise untouched.
 */
export const useCanvasInteraction = (containerRef: RefObject<HTMLElement | null>): CanvasInteractionController => {
  const { scene, events, frames, animator } = useEngine();
  const stateRef = useRef<InteractionState>(IDLE);
  const modifiers = useRef<Modifiers>({ space: false, shift: false, alt: false });
  const pointers = useRef(new Map<number, Vec2>());
  const pinching = useRef(false);
  const rectRef = useRef<DOMRect | null>(null);
  /** Displacement already applied to the dragged objects (post-snap), so moves can be diffed. */
  const appliedDelta = useRef<Vec2>({ x: 0, y: 0 });
  /** Snap candidates captured once at drag start; the moving set's own bounds excluded. */
  const snapTargets = useRef<readonly Bounds[]>([]);
  const dragStartBounds = useRef<Bounds | null>(null);
  /** Whether the current gesture has dispatched a command (so Escape can undo it). */
  const gestureDirty = useRef(false);

  // The canvas rect is read once per gesture (and on resize), never per pointer move.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      rectRef.current = el.getBoundingClientRect();
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', measure);
    };
  }, [containerRef]);

  const toScreen = useCallback((e: { clientX: number; clientY: number }): Vec2 => {
    const rect = rectRef.current;
    return { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) };
  }, []);

  const hitAt = useCallback(
    (world: Vec2): ObjectId | null => {
      const [top] = scene.hitTest(world);
      return top ?? null;
    },
    [scene],
  );

  /** Parent-frame inverse for an object, so handle deltas are measured in the right space. */
  const parentInverse = useCallback(
    (parentId: ObjectId | null) =>
      parentId === null
        ? IDENTITY_MATRIX
        : (invert(scene.worldMatrixOf(parentId) ?? IDENTITY_MATRIX) ?? IDENTITY_MATRIX),
    [scene],
  );

  const beginDragSnapshot = useCallback(
    (ids: readonly ObjectId[]) => {
      const doc = selectActiveDocument(useWorkspaceStore.getState());
      const moving = new Set(doc ? collectSubtrees(doc, ids) : ids);
      const union = unionBounds(ids.map((id) => scene.boundsOf(id)).filter((b): b is Bounds => !!b));
      dragStartBounds.current = union;
      appliedDelta.current = { x: 0, y: 0 };
      if (!union) {
        snapTargets.current = [];
        return;
      }
      // Only nearby, visible, non-moving objects are snap candidates.
      const zoom = useCameraStore.getState().camera.zoom;
      const reach = expand(union, 2000 / zoom);
      snapTargets.current = scene
        .queryBounds(reach)
        .filter((id) => !moving.has(id) && scene.isInteractive(id))
        .map((id) => scene.boundsOf(id))
        .filter((b): b is Bounds => !!b);
    },
    [scene],
  );

  const applyEffects = useCallback(
    (effects: readonly InteractionEffect[]) => {
      const camera = useCameraStore.getState();
      const selection = useSelectionStore.getState();
      const interaction = useInteractionStore.getState();
      const workspace = useWorkspaceStore.getState();
      const dispatch = (cmd: ReturnType<typeof setTransformCommand>) => {
        if (!cmd) return;
        workspace.dispatch(cmd);
        gestureDirty.current = true;
      };
      for (const effect of effects) {
        switch (effect.kind) {
          case 'hover':
            interaction.setHovered(effect.objectId);
            break;
          case 'pan':
            animator.cancel();
            camera.panByScreenDelta(effect.screenDelta);
            break;
          case 'select':
            selection.select(effect.objectIds, effect.additive);
            if (!effect.additive && effect.objectIds.length === 0) useWindowStore.getState().blur();
            break;
          case 'toggle-select':
            selection.toggle(effect.objectId);
            break;
          case 'move-objects': {
            if (dragStartBounds.current === null) beginDragSnapshot(effect.objectIds);
            const start = dragStartBounds.current;
            let total = effect.totalWorldDelta;
            if (start && !modifiers.current.alt) {
              const proposed = shiftBounds(start, total);
              const snap = snapBounds(proposed, snapTargets.current, {
                threshold: SNAP_TOLERANCE_PX / camera.camera.zoom,
                gridSize: GRID_SIZE,
                snapToObjects: true,
                snapToGrid: true,
              });
              total = { x: total.x + snap.delta.x, y: total.y + snap.delta.y };
              interaction.setGuides(snap.guides);
            } else {
              interaction.setGuides([]);
            }
            const step = { x: total.x - appliedDelta.current.x, y: total.y - appliedDelta.current.y };
            if (step.x !== 0 || step.y !== 0) {
              appliedDelta.current = total;
              dispatch(moveObjectsCommand(effect.objectIds, step));
            }
            break;
          }
          case 'drag-end': {
            const doc = selectActiveDocument(workspace);
            const moved = appliedDelta.current.x !== 0 || appliedDelta.current.y !== 0;
            if (doc && moved) dispatch(bringToFrontCommand(doc, effect.objectIds));
            interaction.setGuides([]);
            dragStartBounds.current = null;
            break;
          }
          case 'marquee-update':
            selection.setMarquee(marqueeBounds(effect.startWorld, effect.currentWorld));
            break;
          case 'marquee-end': {
            const bounds = marqueeBounds(effect.startWorld, effect.endWorld);
            selection.setMarquee(null);
            if (isMeaningfulMarquee(bounds)) {
              // Interactive objects only, and never a child together with its selected ancestor.
              const hits = scene.queryBounds(bounds).filter((id) => scene.isInteractive(id));
              const doc = selectActiveDocument(workspace);
              selection.select(doc ? topLevelIds(doc, hits) : hits, effect.additive);
            }
            break;
          }
          case 'resize': {
            const doc = selectActiveDocument(workspace);
            const obj = doc?.objects[effect.objectId];
            if (!doc || !obj) break;
            const inv = parentInverse(obj.parentId);
            const result = resizeFromHandle(
              {
                position: effect.start.transform.position,
                size: effect.start.size,
                rotation: effect.start.transform.rotation,
                scale: effect.start.transform.scale,
              },
              effect.handle,
              applyMatrix(inv, effect.startWorld),
              applyMatrix(inv, effect.currentWorld),
              objectType(obj.kind).minSize,
              effect.keepAspect,
            );
            dispatch(setTransformCommand(doc, obj.id, result, 'Resize object'));
            break;
          }
          case 'rotate': {
            const doc = selectActiveDocument(workspace);
            const obj = doc?.objects[effect.objectId];
            if (!doc || !obj) break;
            const inv = parentInverse(obj.parentId);
            const rotation = rotationFromDrag(
              boxCentre(effect.start.transform, effect.start.size),
              effect.start.transform.rotation,
              applyMatrix(inv, effect.startWorld),
              applyMatrix(inv, effect.currentWorld),
              effect.snap ? Math.PI / 12 : null,
            );
            dispatch(setTransformCommand(doc, obj.id, { rotation }, 'Rotate object'));
            break;
          }
          case 'manipulation-end':
            break;
          case 'connect-update':
            interaction.setConnect({ sourceId: effect.sourceId, to: effect.currentWorld, targetId: effect.targetId });
            break;
          case 'connect-end': {
            interaction.setConnect(null);
            const doc = selectActiveDocument(workspace);
            if (!doc || !effect.targetId) break;
            if (effect.reconnect) {
              dispatch(reconnectCommand(doc, effect.reconnect.connectionId, effect.reconnect.end, effect.targetId));
            } else {
              dispatch(addConnectionCommand(doc, effect.sourceId, effect.targetId));
            }
            break;
          }
          case 'gesture-cancelled':
            // Every gesture merges into one history entry, so one undo restores the pre-gesture document.
            if (gestureDirty.current) workspace.undo();
            selection.setMarquee(null);
            interaction.setGuides([]);
            interaction.setConnect(null);
            dragStartBounds.current = null;
            break;
        }
      }
    },
    [scene, beginDragSnapshot, parentInverse, animator],
  );

  const applyEffectsRef = useRef(applyEffects);
  useEffect(() => {
    applyEffectsRef.current = applyEffects;
  }, [applyEffects]);

  const transition = useCallback((input: Parameters<typeof reduceInteraction>[1]) => {
    const wasGesture = isGesture(stateRef.current);
    const { state, effects } = reduceInteraction(stateRef.current, input);
    stateRef.current = state;
    if (!wasGesture && isGesture(state)) gestureDirty.current = false;
    applyEffectsRef.current(effects);
    useInteractionStore.getState().setMode(state.mode);
  }, []);

  // ---- keyboard modifiers and Escape ------------------------------------
  useEffect(() => {
    const editable = (t: EventTarget | null) => t instanceof HTMLTextAreaElement || t instanceof HTMLInputElement;
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Shift') modifiers.current.shift = true;
      if (e.key === 'Alt') modifiers.current.alt = true;
      if (e.code === 'Space' && !editable(e.target)) modifiers.current.space = true;
      if (e.key === 'Escape' && isGesture(stateRef.current)) {
        // Capture phase on window: runs before the global shortcut handler and swallows the key.
        e.stopPropagation();
        e.preventDefault();
        transition({ kind: 'cancel' });
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === 'Shift') modifiers.current.shift = false;
      if (e.key === 'Alt') modifiers.current.alt = false;
      if (e.code === 'Space') modifiers.current.space = false;
    };
    const blur = () => {
      modifiers.current = { space: false, shift: false, alt: false };
    };
    window.addEventListener('keydown', down, true);
    window.addEventListener('keyup', up, true);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down, true);
      window.removeEventListener('keyup', up, true);
      window.removeEventListener('blur', blur);
    };
  }, [transition]);

  // ---- pointer plumbing ----------------------------------------------------

  const deliverMove = useCallback(
    ({ pointerId, screen }: MoveSample) => {
      if (pinching.current) {
        const prev = pinchPoints(pointers.current);
        pointers.current.set(pointerId, screen);
        const next = pinchPoints(pointers.current);
        if (prev && next) {
          animator.cancel();
          useCameraStore.getState().pinch(prev, next);
        }
        return;
      }
      pointers.current.set(pointerId, screen);
      const world = screenToWorld(screen, useCameraStore.getState().camera);
      events.emit({ type: 'pointer:world', world, screen });
      const mode = stateRef.current.mode;
      // Hit-test only when the machine needs it (hover tracking, connect targets).
      const needsHit = mode === 'idle' || mode === 'hover' || mode === 'connect';
      transition({
        kind: 'pointer-move',
        pointerId,
        screen,
        world,
        shiftHeld: modifiers.current.shift,
        ...(needsHit ? { hit: hitAt(world) } : {}),
      });
    },
    [events, transition, hitAt, animator],
  );

  const deliverRef = useRef(deliverMove);
  useEffect(() => {
    deliverRef.current = deliverMove;
  }, [deliverMove]);

  // Created in an effect (not render) so the deliver closure may read the latest ref.
  const coalescerRef = useRef<PointerCoalescer<MoveSample> | null>(null);
  useEffect(() => {
    const coalescer = new PointerCoalescer<MoveSample>(
      (flush) => {
        frames.schedule(flush);
      },
      (sample) => {
        deliverRef.current(sample);
      },
      (sample) => sample.pointerId,
    );
    coalescerRef.current = coalescer;
    return () => {
      coalescer.clear();
      coalescerRef.current = null;
    };
  }, [frames]);

  const beginPinch = useCallback(() => {
    if (pinching.current) return;
    pinching.current = true;
    useSelectionStore.getState().setMarquee(null);
    transition({ kind: 'cancel' });
  }, [transition]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button !== 0 && e.button !== 1) return;
      rectRef.current = e.currentTarget.getBoundingClientRect();
      const screen = toScreen(e);
      pointers.current.set(e.pointerId, screen);
      e.currentTarget.setPointerCapture(e.pointerId);

      if (e.pointerType === 'touch' && pointers.current.size >= 2) {
        beginPinch();
        return;
      }
      if (pinching.current) return;

      const world = screenToWorld(screen, useCameraStore.getState().camera);
      const hit = hitAt(world);
      if (hit === null && e.button === 0 && !modifiers.current.space) {
        // Connections are thin: test them only when nothing solid is under the pointer.
        const doc = selectActiveDocument(useWorkspaceStore.getState());
        const tolerance = CONNECTION_HIT_PX / useCameraStore.getState().camera.zoom;
        const con =
          doc &&
          hitTestConnections(
            doc,
            world,
            tolerance,
            (id) => scene.visibleAnchorOf(id),
            (id) => scene.boundsOf(id),
          );
        if (con) {
          useSelectionStore.getState().selectConnections([con], e.shiftKey);
          useWindowStore.getState().blur();
          return;
        }
      }
      transition({
        kind: 'pointer-down',
        pointerId: e.pointerId,
        screen,
        world,
        hit,
        hitLocked: hit !== null && (scene.get(hit)?.locked ?? false),
        selected: useSelectionStore.getState().selection.objectIds,
        // A single finger on empty space pans; marquee on touch is a later gesture.
        spaceHeld: modifiers.current.space || (e.pointerType === 'touch' && hit === null),
        shiftHeld: e.shiftKey,
        altHeld: e.altKey,
        button: e.button,
      });
    },
    [toScreen, transition, hitAt, beginPinch, scene],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (!pinching.current && !isGesture(stateRef.current) && e.pointerType !== 'mouse') return;
      coalescerRef.current?.push({ pointerId: e.pointerId, screen: toScreen(e) });
    },
    [toScreen],
  );

  const endPointer = useCallback(
    (e: ReactPointerEvent<HTMLElement>, cancelled: boolean) => {
      coalescerRef.current?.flush();
      pointers.current.delete(e.pointerId);
      if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
      if (pinching.current) {
        if (pointers.current.size < 2) pinching.current = false;
        return;
      }
      transition(cancelled ? { kind: 'cancel' } : { kind: 'pointer-up', pointerId: e.pointerId });
    },
    [transition],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      endPointer(e, false);
    },
    [endPointer],
  );
  const onPointerCancel = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      endPointer(e, true);
    },
    [endPointer],
  );
  const onPointerLeave = useCallback(() => {
    if (!isGesture(stateRef.current)) transition({ kind: 'pointer-leave' });
  }, [transition]);
  const onLostPointerCapture = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      // Capture can be stolen (e.g. by the browser on a system gesture). Treat like cancel.
      if (pointers.current.has(e.pointerId)) endPointer(e, true);
    },
    [endPointer],
  );

  // Wheel must be a non-passive native listener so ctrl+wheel can suppress browser zoom.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      animator.cancel();
      const camera = useCameraStore.getState();
      if (e.ctrlKey || e.metaKey) {
        const factor = Math.exp(-e.deltaY * CAMERA_LIMITS.wheelZoomSensitivity);
        camera.zoomAt(camera.camera.zoom * factor, toScreen(e));
      } else {
        camera.panByScreenDelta({ x: -e.deltaX, y: -e.deltaY });
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, [containerRef, toScreen, animator]);

  const beginHandleDrag = useCallback(
    (e: ReactPointerEvent<HTMLElement>, objectId: ObjectId, handle: ResizeHandle | 'rotate') => {
      const obj = scene.get(objectId);
      const root = containerRef.current;
      if (!obj || obj.locked || !root) return;
      e.stopPropagation();
      e.preventDefault();
      rectRef.current = root.getBoundingClientRect();
      const screen = toScreen(e);
      pointers.current.set(e.pointerId, screen);
      root.setPointerCapture(e.pointerId);
      transition({
        kind: 'handle-down',
        pointerId: e.pointerId,
        world: screenToWorld(screen, useCameraStore.getState().camera),
        objectId,
        handle,
        start: { transform: obj.transform, size: obj.size },
      });
    },
    [scene, containerRef, toScreen, transition],
  );

  const beginReconnect = useCallback(
    (e: ReactPointerEvent<HTMLElement>, connectionId: ConnectionId, end: Reconnect['end']) => {
      const doc = selectActiveDocument(useWorkspaceStore.getState());
      const c = doc?.connections[connectionId];
      const root = containerRef.current;
      if (!c || !root) return;
      e.stopPropagation();
      e.preventDefault();
      rectRef.current = root.getBoundingClientRect();
      const screen = toScreen(e);
      pointers.current.set(e.pointerId, screen);
      root.setPointerCapture(e.pointerId);
      transition({
        kind: 'reconnect-down',
        pointerId: e.pointerId,
        world: screenToWorld(screen, useCameraStore.getState().camera),
        connectionId,
        end,
        fixedId: end === 'source' ? c.targetId : c.sourceId,
      });
    },
    [containerRef, toScreen, transition],
  );

  return {
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave, onLostPointerCapture },
    beginHandleDrag,
    beginReconnect,
  };
};
